import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import {
  OPEN_PREFERENCES_EVENT,
  getAdConsent,
  hasGlobalPrivacyControl,
  hasMadeConsentChoice,
  setAdConsent,
  type ConsentValue,
} from "@/lib/consent";

/**
 * US opt-out cookie notice. Advertising cookies run by default, but visitors
 * can opt out here, and a Global Privacy Control browser signal opts them out
 * automatically. Reopened from the footer's "Your Privacy Choices" link.
 */
const CookieNotice = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");
  const [visible, setVisible] = useState(false);
  const [gpc, setGpc] = useState(false);
  const [current, setCurrent] = useState<ConsentValue>("granted");

  useEffect(() => {
    setGpc(hasGlobalPrivacyControl());
    setCurrent(getAdConsent());
    if (!hasMadeConsentChoice()) setVisible(true);

    const open = () => {
      setGpc(hasGlobalPrivacyControl());
      setCurrent(getAdConsent());
      setVisible(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, open);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open);
  }, []);

  const choose = useCallback((value: ConsentValue) => {
    setAdConsent(value);
    setCurrent(value);
    setVisible(false);
  }, []);

  if (!visible || isAdmin) return null;

  return (
    <div
      role="region"
      aria-label="Cookie preferences"
      className="fixed bottom-0 inset-x-0 z-[70] px-4 pb-4 pointer-events-none"
      // Keep clicks here from reaching any open dialog's "click outside" handler.
      onPointerDownCapture={(e) => e.nativeEvent.stopImmediatePropagation()}
      onPointerDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
      onMouseDown={(e) => e.stopPropagation()}
      onFocusCapture={(e) => e.stopPropagation()}
    >
      <div className="pointer-events-auto mx-auto max-w-4xl rounded-2xl border border-brand-linen bg-card/95 backdrop-blur-sm shadow-lg px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
        <Cookie className="w-5 h-5 text-primary shrink-0 hidden sm:block" strokeWidth={1.5} />
        <div className="flex-1 space-y-1">
          <p className="text-xs sm:text-[13px] font-sans font-light text-brand-driftwood leading-relaxed">
            We use cookies for site functionality and advertising. Opt out anytime — see our{" "}
            <Link to="/privacy-policy" className="text-primary underline underline-offset-2">
              Privacy Policy
            </Link>
            .
          </p>
          {gpc && (
            <p className="text-[11px] font-sans text-brand-stone leading-relaxed">
              Your browser is sending a Global Privacy Control signal, so advertising cookies are
              already turned off.
            </p>
          )}
          {!gpc && hasMadeConsentChoice() && (
            <p className="text-[11px] font-sans text-brand-stone leading-relaxed">
              Advertising cookies are currently{" "}
              <strong>{current === "denied" ? "off" : "on"}</strong>.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => choose("denied")}
            disabled={gpc}
            className="px-4 py-2.5 border border-brand-linen text-brand-driftwood text-[11px] tracking-[0.14em] uppercase font-sans font-semibold rounded-pill hover:border-primary hover:text-primary active:scale-[0.97] transition-all disabled:opacity-50 disabled:pointer-events-none"
          >
            Opt Out
          </button>
          <button
            onClick={() => choose("granted")}
            disabled={gpc}
            className="px-5 py-2.5 bg-primary text-primary-foreground text-[11px] tracking-[0.14em] uppercase font-sans font-semibold rounded-pill hover:shadow-brand-gold hover:-translate-y-px active:scale-[0.97] transition-all disabled:opacity-50 disabled:pointer-events-none"
          >
            Accept
          </button>
          <button
            onClick={() => setVisible(false)}
            aria-label="Close cookie preferences"
            className="p-2 text-brand-stone hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieNotice;
